import { useEffect, useState } from 'react';
import { chrome as Api, type Footer, type Header } from '@tvx/api-client';
import { useAsync } from '../lib/useAsync';
import { Shim } from '../components/Shimmer';
import { Field, Row } from '../components/Field';

/**
 * The header and the footer — the two things every page on the site wears.
 *
 * These had a screen in the generated console and lost it in the React port:
 * the API was still there, nothing called it, and for a while the menu, the
 * social links and the footer columns could not be changed at all.
 * Editing them is rare, which is exactly why nobody noticed.
 *
 * Rows are edited in place rather than through a modal. A menu is a list you
 * reorder while looking at it, and a dialog over the top of it hides the thing
 * being changed.
 */
export function Chrome() {
  const { data, error, loading, reload } = useAsync((signal) => Api.load(signal));
  const [header, setHeader] = useState<Header | null>(null);
  const [footer, setFooter] = useState<Footer | null>(null);
  const [logo, setLogo] = useState('');
  const [status, setStatus] = useState('');
  const [tab, setTab] = useState<'header' | 'footer'>('header');

  useEffect(() => {
    if (!data) return;
    setHeader(structuredClone(data.header));
    setFooter(structuredClone(data.footer));
    setLogo(data.logo_path || '');
  }, [data]);

  if (error) return <div className="err">{error.message}</div>;
  if (loading || !data || !header || !footer) return <div style={{ display: 'grid', gap: 12 }}><Shim w="46%" h={16} /><Shim w="78%" /><Shim w="62%" /></div>;

  async function save(part: 'header' | 'footer') {
    setStatus('Saving…');
    try {
      await Api.save(part, part === 'header' ? header : { ...footer, logo_path: logo });
      setStatus(`Saved · live on the site`);
      reload();
    } catch (e) {
      setStatus((e as Error).message);
    }
  }

  async function reset(part: 'header' | 'footer') {
    if (!window.confirm(`Put the design's own ${part} back? Your edits to it are lost.`)) return;
    await Api.reset(part);
    setStatus(`The design's ${part} is back`);
    reload();
  }

  return (
    <>
      <div className="rowbar">
        <div>
          <h1 style={{ fontSize: 27, margin: 0 }}>Header &amp; footer</h1>
          <p className="muted" style={{ fontSize: 13.5, marginTop: 6 }}>
            The menu and the footer columns — what every page on the site wears.
            Saved here, they replace the design's own; reset puts the design's back.
          </p>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <a className="btn2 bo" href={data.site_url} target="_blank" rel="noreferrer"
            style={{ textDecoration: 'none' }}>View site ↗</a>
          <button className="btn2 bg" onClick={() => save(tab)}>Save {tab}</button>
        </div>
      </div>

      <div style={{ display: 'flex', gap: 8, marginBottom: 18 }}>
        {(['header', 'footer'] as const).map((t) => (
          <button key={t} className={`btn2 ${tab === t ? 'bg' : 'bo'}`} onClick={() => setTab(t)}>
            {t === 'header' ? 'Header' : 'Footer'}
            {data.custom[t] ? ' · edited' : ' · as designed'}
          </button>
        ))}
      </div>

      {tab === 'header' ? (
        <div style={{ display: 'grid', gap: 16 }}>
          <div className="crd" style={{ padding: '20px 22px' }}>
            <div className="kh" style={{ marginBottom: 14 }}>The menu</div>
            {header.nav.map((item, i) => (
              <div key={i} className="crd" style={{ padding: '14px 16px', marginBottom: 10 }}>
                <Row cols="1fr 1fr auto">
                  <Field label="Label"><input className="inp" value={item.label}
                    onChange={(e) => setHeader(edit(header, (h) => { h.nav[i]!.label = e.target.value; }))} /></Field>
                  <Field label="Link" hint="A path on the site, like /pricing/">
                    <input className="inp" value={item.href}
                      onChange={(e) => setHeader(edit(header, (h) => { h.nav[i]!.href = e.target.value; }))} /></Field>
                  <Field label=" ">
                    <button className="btn2 bo" onClick={() => setHeader(edit(header, (h) => { h.nav.splice(i, 1); }))}>Remove</button>
                  </Field>
                </Row>

                {item.children.length ? (
                  <div style={{ marginTop: 10, paddingLeft: 14, borderLeft: '1px solid var(--line)' }}>
                    {item.children.map((c, ci) => (
                      <Row key={ci} cols="1fr 1fr 90px auto">
                        <Field label="Sub-item"><input className="inp" value={c.label}
                          onChange={(e) => setHeader(edit(header, (h) => { h.nav[i]!.children[ci]!.label = e.target.value; }))} /></Field>
                        <Field label="Link"><input className="inp" value={c.href}
                          onChange={(e) => setHeader(edit(header, (h) => { h.nav[i]!.children[ci]!.href = e.target.value; }))} /></Field>
                        <Field label="Tag" hint="e.g. Paid"><input className="inp" value={c.pill}
                          onChange={(e) => setHeader(edit(header, (h) => { h.nav[i]!.children[ci]!.pill = e.target.value; }))} /></Field>
                        <Field label=" ">
                          <button className="btn2 bo" onClick={() => setHeader(edit(header, (h) => { h.nav[i]!.children.splice(ci, 1); }))}>×</button>
                        </Field>
                      </Row>
                    ))}
                  </div>
                ) : null}

                <button className="btn2 bo" style={{ marginTop: 8 }}
                  onClick={() => setHeader(edit(header, (h) => { h.nav[i]!.children.push({ label: '', href: '/', pill: '' }); }))}>
                  Add a sub-item
                </button>
              </div>
            ))}
            <button className="btn2 bo"
              onClick={() => setHeader(edit(header, (h) => { h.nav.push({ label: '', href: '/', children: [] }); }))}>
              Add a menu item
            </button>
          </div>

          

          <div className="crd" style={{ padding: '20px 22px' }}>
            <div className="kh" style={{ marginBottom: 14 }}>The two buttons</div>
            <Row cols={2}>
              <Field label="Sign-in button"><input className="inp" value={header.cta_login}
                onChange={(e) => setHeader(edit(header, (h) => { h.cta_login = e.target.value; }))} /></Field>
              <Field label="Join button"><input className="inp" value={header.cta_join}
                onChange={(e) => setHeader(edit(header, (h) => { h.cta_join = e.target.value; }))} /></Field>
            </Row>
          </div>

          <button className="btn2 bo" style={{ justifySelf: 'start' }} onClick={() => reset('header')}>
            Put the design's header back
          </button>
        </div>
      ) : (
        <div style={{ display: 'grid', gap: 16 }}>
          <div className="crd" style={{ padding: '20px 22px' }}>
            <div className="kh" style={{ marginBottom: 8 }}>Footer Links</div>
            <p className="muted" style={{ fontSize: 13, marginBottom: 18 }}>
              Configure the Community and Open Account links displayed across the website footer.
            </p>

            <div style={{ marginBottom: 20 }}>
              <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 10, color: 'var(--tx1)' }}>Community</div>
              <Row cols={2}>
                <Field label="Indian" hint="URL for Indian Community">
                  <input
                    className="inp"
                    value={footer.community?.indian ?? footer.community_indian ?? ''}
                    placeholder="https://t.me/..."
                    onChange={(e) => {
                      const val = e.target.value;
                      setFooter(edit(footer, (f) => {
                        if (!f.community) f.community = { indian: '', forex: '' };
                        f.community.indian = val;
                        f.community_indian = val;
                      }));
                    }}
                  />
                </Field>
                <Field label="Forex" hint="URL for Forex Community. Shown on the website only while Forex Signals is on for the website (Settings → Modules).">
                  <input
                    className="inp"
                    value={footer.community?.forex ?? footer.community_forex ?? ''}
                    placeholder="https://t.me/..."
                    onChange={(e) => {
                      const val = e.target.value;
                      setFooter(edit(footer, (f) => {
                        if (!f.community) f.community = { indian: '', forex: '' };
                        f.community.forex = val;
                        f.community_forex = val;
                      }));
                    }}
                  />
                </Field>
              </Row>
            </div>

            <div>
              <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 10, color: 'var(--tx1)' }}>Open Account</div>
              <Row cols={2}>
                <Field label="Indian" hint="URL for Indian Open Account">
                  <input
                    className="inp"
                    value={footer.open_account?.indian ?? footer.open_account_indian ?? ''}
                    placeholder="https://..."
                    onChange={(e) => {
                      const val = e.target.value;
                      setFooter(edit(footer, (f) => {
                        if (!f.open_account) f.open_account = { indian: '', forex: '' };
                        f.open_account.indian = val;
                        f.open_account_indian = val;
                      }));
                    }}
                  />
                </Field>
                <Field label="Forex" hint="URL for Forex Open Account. Shown on the website only while Forex Signals is on for the website (Settings → Modules).">
                  <input
                    className="inp"
                    value={footer.open_account?.forex ?? footer.open_account_forex ?? ''}
                    placeholder="https://..."
                    onChange={(e) => {
                      const val = e.target.value;
                      setFooter(edit(footer, (f) => {
                        if (!f.open_account) f.open_account = { indian: '', forex: '' };
                        f.open_account.forex = val;
                        f.open_account_forex = val;
                      }));
                    }}
                  />
                </Field>
              </Row>
            </div>
          </div>

          <div className="crd" style={{ padding: '20px 22px' }}>
            <div className="kh" style={{ marginBottom: 14 }}>The wordmark</div>
            <Field label="Logo image"
              hint={`A path under /media/. Left empty the design's own wordmark is used (${data.logo_default}).`}>
              <input className="inp" value={logo} placeholder={data.logo_default}
                onChange={(e) => setLogo(e.target.value)} />
            </Field>
            <img src={`${data.site_url}${logo || data.logo_default}`} alt=""
              style={{ height: 34, marginTop: 8 }} />
          </div>

          <div className="crd" style={{ padding: '20px 22px' }}>
            <div className="kh" style={{ marginBottom: 14 }}>Social chips</div>
            <p className="muted" style={{ fontSize: 12, marginBottom: 14 }}>
              A chip with no link renders as plain text, exactly as the design draws it — nobody
              taps through to nothing.
            </p>
            {footer.social.map((s, i) => (
              <Row key={i} cols="1fr 2fr auto">
                <Field label="Label"><input className="inp" value={s.label}
                  onChange={(e) => setFooter(edit(footer, (f) => { f.social[i]!.label = e.target.value; }))} /></Field>
                <Field label="Link"><input className="inp" value={s.href} placeholder="https://…"
                  onChange={(e) => setFooter(edit(footer, (f) => { f.social[i]!.href = e.target.value; }))} /></Field>
                <Field label=" ">
                  <button className="btn2 bo" onClick={() => setFooter(edit(footer, (f) => { f.social.splice(i, 1); }))}>×</button>
                </Field>
              </Row>
            ))}
            <button className="btn2 bo" style={{ marginTop: 8 }}
              onClick={() => setFooter(edit(footer, (f) => { f.social.push({ label: '', href: '' }); }))}>
              Add a chip
            </button>
          </div>

          {footer.columns.map((col, ci) => (
            <div key={ci} className="crd" style={{ padding: '20px 22px' }}>
              <Row cols="1fr auto">
                <Field label="Column heading"><input className="inp" value={col.heading}
                  onChange={(e) => setFooter(edit(footer, (f) => { f.columns[ci]!.heading = e.target.value; }))} /></Field>
                <Field label=" ">
                  <button className="btn2 bo" onClick={() => setFooter(edit(footer, (f) => { f.columns.splice(ci, 1); }))}>Remove column</button>
                </Field>
              </Row>
              {col.links.map((l, li) => (
                <Row key={li} cols="1fr 1fr auto">
                  <Field label="Label"><input className="inp" value={l.label}
                    onChange={(e) => setFooter(edit(footer, (f) => { f.columns[ci]!.links[li]!.label = e.target.value; }))} /></Field>
                  <Field label="Link"><input className="inp" value={l.href}
                    onChange={(e) => setFooter(edit(footer, (f) => { f.columns[ci]!.links[li]!.href = e.target.value; }))} /></Field>
                  <Field label=" ">
                    <button className="btn2 bo" onClick={() => setFooter(edit(footer, (f) => { f.columns[ci]!.links.splice(li, 1); }))}>×</button>
                  </Field>
                </Row>
              ))}
              <button className="btn2 bo" style={{ marginTop: 8 }}
                onClick={() => setFooter(edit(footer, (f) => { f.columns[ci]!.links.push({ label: '', href: '/' }); }))}>
                Add a link
              </button>
            </div>
          ))}

          <div style={{ display: 'flex', gap: 10 }}>
            <button className="btn2 bo"
              onClick={() => setFooter(edit(footer, (f) => { f.columns.push({ heading: '', links: [] }); }))}>
              Add a column
            </button>
            <button className="btn2 bo" onClick={() => reset('footer')}>Put the design's footer back</button>
          </div>

          <p className="muted" style={{ fontSize: 12 }}>
            Terms, privacy and refunds are not edited here — they sit beside the copyright line and
            follow whichever legal pages are published.
          </p>
        </div>
      )}

      {status ? <p className="muted" style={{ fontSize: 12.5, marginTop: 14 }}>{status}</p> : null}
    </>
  );
}

/** Clone, mutate, return — so React sees a new object and re-renders. */
function edit<T>(value: T, change: (draft: T) => void): T {
  const next = structuredClone(value);
  change(next);
  return next;
}
